/* eslint-disable no-alert */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Formik, Form, Field } from 'formik';
import { selectBox, textArea } from '../inputs';
import { validations } from '../../helperFunctions';
import { TextArea } from '../../modules/components/inputs';
import { affectsOptions } from './DeleteAppointment';

// ======================== NOTE WHEN MAKING CHANGES TO THIS COMPONENT: Consider DeleteAppointment as well, as they are kinda related ========================
const CancelReason = ({
  id,
  setLoader,
  closeModal,
  getEvents,
  role,
  pathway,
  changerId,
  organizationID,
  customClass,
  pageName,
  cancelOnProvider = false,
  cancellationPollingTimeout = 600,
  patientId = '',
  stringValues = {},
  classes = {},
  isRecurring = false,
  cancellationProcess, // a function that returns a promise which will cancel the appointment
}) => {
  const [selectedOption, setSelectedOption] = useState(false);
  const [statusChangeOptions, setStatusChangeOptions] = useState({ data: [] });
  useEffect(() => {
    statusChangeReasons();
  }, []);
  const startTime = React.useRef();

  const statusChangeReasons = () => {
    const headers = {
      'X-CSRF-Token': $('meta[name="csrf-token"]').attr('content'),
    };

    const data = {
      role: role || 'provider_or_scheduling_admin',
      organization_id: organizationID,
    };

    axios
      .get(
        `/open_filters/status_change_reason?${data.role}`,
        { params: data },
        {
          headers,
        },
      )
      .then((res) => {
        console.log('Created', res.data);
        setLoader(false);
        setStatusChangeOptions(res.data);
      })
      .catch((err) => {
        setLoader(false);
        const msg =
          err.response.data && err.response.data.message
            ? err.response.data.message
            : err;
        alert(msg);
      });
  };

  const roleIsMember = validations.isNotNull(role) && role === 'member';
  const isSecuredMessage = pathway === 'secured_message';

  const continueHandleSubmit = (values) => {
    const headers = {
      'X-CSRF-Token': $('meta[name="csrf-token"]').attr('content'),
    };
    const { cancel_reason, comments, affects } = values;
    const data = {
      status: 'cancelled',
      changed_at: new Date().toISOString(),
      encounter_id: id,
      status_change_reason_id: cancel_reason,
      comments,
      affects,
      role: role || 'provider_or_scheduling_admin',
    };
    if (pathway) {
      data.pathway = pathway;
    }
    if (changerId) {
      data.changer_id = changerId;
    }
    let statusChangeURL = `/api/v1/visits/${id}/status_change`;
    let cancelVisitURL = `/api/v1/visits/${id}`;
    if (roleIsMember) {
      if (isSecuredMessage) {
        cancelVisitURL = null;
      } else {
        statusChangeURL = `/member_self_serve/status_change?id=${id}`;
        cancelVisitURL = `/member_self_serve/update_status?id=${id}`;
      }
    }

    // Either one of cancelVisitURL or cancellationProcess should be present!
    // cancellationProcess is used in the appointment confirmation page right now

    const cancelVisit =
      cancellationProcess ||
      (() =>
        new Promise((resolve, reject) => {
          const visitCancelData = {
            visit_status: 'cancelled',
            pageName: pageName || 'Scheduling Page',
            no_status_change_record_creation: true,
          };
          if (isRecurring) visitCancelData.affects = affects;
          setLoader(true);
          axios
            .patch(cancelVisitURL, visitCancelData, { headers })
            .then(() => {
              getEvents();
              resolve();
            })
            .catch((err) => {
              const msg =
                err.response.data && err.response.data.message
                  ? err.response.data.message
                  : err;
              alert(msg);
              reject(msg);
            })
            .finally(() => setLoader(false));
        }));

    cancelVisit().then(() => {
      // vvvvvvvvvv Status Change Record Creation vvvvvvvvvv
      axios
        .post(statusChangeURL, data, {
          headers,
        })
        .then((res) => {
          console.log('Created', res.data);
          closeModal('success');
          return res.data;
        })
        .catch((err) => {
          setLoader(false);
          const msg =
            err.response.data && err.response.data.message
              ? err.response.data.message
              : err;
          alert(msg);
        });
      // ^^^^^^^^ Status Change Record Creation ^^^^^^^^
    });
  };

  const pollingCall = (transactionId, values) => {
    axios
      .get(`/call_centers/transaction_confirmation`, {
        params: {
          transaction_id: transactionId,
          patient_id: patientId,
        },
      })
      .then((response) => {
        const currentTime = Date.now() / 1000;
        if (response.data.status === 'success') {
          continueHandleSubmit(values);
        } else if (
          currentTime - startTime.current > cancellationPollingTimeout ||
          response.data.status === 'failed'
        ) {
          alert(
            'We’re sorry, but your original appointment can no longer be modified by Lucet. Please contact the provider directly for any appointment changes needed.',
          );
          setLoader(false);
          closeModal();
          getEvents();
        } else {
          pollingCall(transactionId, values);
        }
      });
  };

  const handleVisitCancellationRequest = (values) => {
    const { cancel_reason, comments } = values;
    axios
      .get(`/call_centers/create_visit_cancellation_request_transaction`, {
        params: {
          encounter_id: id,
          patient_id: patientId,
          status: 'cancelled',
          changed_at: new Date().toISOString(),
          comments,
          status_change_reason_id: cancel_reason,
          role: role || 'provider_or_scheduling_admin',
        },
      })
      .then((res) => {
        startTime.current = Date.now() / 1000;
        pollingCall(res.data.transaction_id, values);
      });
  };

  const handleSubmit = (values) => {
    setLoader(true);
    if (cancelOnProvider) {
      handleVisitCancellationRequest(values);
    } else {
      continueHandleSubmit(values);
    }
  };

  const initialValues = {
    cancel_reason: '',
  };

  // ensures option has the remainder option when message is member secured message
  const defaultAffectsOption =
    roleIsMember && isSecuredMessage
      ? affectsOptions.find((option) => option.value === 'remainder').value
      : affectsOptions[0].value;

  const showAffectsField = isRecurring && !(roleIsMember && isSecuredMessage);

  return (
    <React.Fragment>
      <div className={`${customClass}`} style={{ display: 'block' }}>
        <Formik
          initialValues={
            isRecurring
              ? { ...initialValues, affects: defaultAffectsOption }
              : initialValues
          }
          onSubmit={(values, { setSubmitting }) => {
            setTimeout(() => {
              handleSubmit(values);
              setSubmitting(false);
            }, 400);
          }}
        >
          <Form className="cancel-reason-form">
            <h3 className={`text-8 text-center ${classes.header}`}>
              {stringValues.cancel_appointment || 'Cancel Appointment'}
            </h3>
            {showAffectsField && (
              <div className={`row-block ${classes.rowBlock}`}>
                <Field
                  placeholder="Select Option"
                  name="affects"
                  component={selectBox}
                  label="Affects"
                  options={affectsOptions}
                  lucetTheme
                  customSelectClass={classes.customSelectClass}
                  validate={validations.required}
                />
              </div>
            )}
            <div
              className={`row-block ${classes.rowBlock} ${
                showAffectsField ? 'margin-top-lg' : ''
              }`}
            >
              <Field
                placeholder="Select Option"
                name="cancel_reason"
                component={selectBox}
                label={
                  stringValues.why_cancel_this_appt ||
                  'Why are you canceling this appointment?'
                }
                options={statusChangeOptions.data}
                lucetTheme
                onChange={(e) => {
                  statusChangeOptions.data.forEach((element) => {
                    if (element.value === e) {
                      setSelectedOption(element.comment_flag);
                    }
                  });
                }}
                customSelectClass={classes.customSelectClass}
                validate={validations.required}
              />
            </div>
            {selectedOption && (
              <TextArea
                placeholder="Briefly explain why you're canceling."
                name="comments"
                component={textArea}
                label="Comments"
                validate={validations.required}
                className={classes.customTextAreaClass}
                labelClass='label-block'
              />
            )}

            <div className={`modal-footer ${classes.footer}`}>
              <button
                type="button"
                onClick={closeModal}
                className={`btn btn-default lucet-sm-btn ${classes.defaultBtn}`}
              >
                Back
              </button>
              <button
                type="submit"
                className={`btn btn-primary lucet-sm-btn ${classes.primaryBtn}`}
              >
                Save
              </button>
            </div>
          </Form>
        </Formik>
      </div>
    </React.Fragment>
  );
};

export default CancelReason;
